import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, RotateCw, AlertTriangle, CheckCircle, Pencil, Trash2, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import CountrySelect, { DEFAULT_COUNTRY_CODE, getCountryBadgeClass, getCountryLabel, normalizeCountryCode } from '../components/CountrySelect';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { completeDialogSubmit } from '../utils/formUtils';
import { canEditFleetRecord, canHardDelete } from '../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createInitialFormData = () => ({
  serial_number: '',
  brand: '',
  model: '',
  size: '',
  vehicle_id: '',
  position: '',
  country: DEFAULT_COUNTRY_CODE,
  purchase_date: new Date().toISOString().split('T')[0],
  purchase_cost: '',
  currency: 'GHS',
  tread_depth_mm: '',
  notes: '',
});

const tireToFormData = (tire) => {
  const purchaseDate =
    typeof tire.purchase_date === 'string'
      ? tire.purchase_date.split('T')[0]
      : new Date(tire.purchase_date).toISOString().split('T')[0];
  return {
    serial_number: tire.serial_number,
    brand: tire.brand,
    model: tire.model || '',
    size: tire.size,
    vehicle_id: tire.vehicle_id || '',
    position: tire.position || '',
    country: normalizeCountryCode(tire.country),
    purchase_date: purchaseDate,
    purchase_cost: tire.purchase_cost,
    currency: tire.currency,
    tread_depth_mm: tire.tread_depth_mm ?? '',
    notes: tire.notes || '',
  };
};

const POSITIONS = [
  { value: 'FRONT_LEFT', label: 'Front Left (FL)' },
  { value: 'FRONT_RIGHT', label: 'Front Right (FR)' },
  { value: 'REAR_LEFT', label: 'Rear Left (RL)' },
  { value: 'REAR_RIGHT', label: 'Rear Right (RR)' },
  { value: 'SPARE', label: 'Spare' },
];

const TireManagement = () => {
  const { user } = useAuth();
  const canEdit = canEditFleetRecord(user?.role);
  const canDelete = canHardDelete(user?.role, 'tire');

  const [tires, setTires] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [rotationDialogOpen, setRotationDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCountry, setBulkCountry] = useState(DEFAULT_COUNTRY_CODE);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [formData, setFormData] = useState(createInitialFormData);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tiresRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/tires`),
        axios.get(`${API}/vehicles`),
      ]);
      setTires(tiresRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      toast.error('Failed to load tire data');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingId(null);
      setFormData(createInitialFormData());
    }
  };

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData(createInitialFormData());
    setDialogOpen(true);
  };

  const openEditDialog = (tire) => {
    setEditingId(tire.id);
    setFormData(tireToFormData(tire));
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      purchase_date: new Date(formData.purchase_date).toISOString(),
      purchase_cost: parseFloat(formData.purchase_cost),
      tread_depth_mm: formData.tread_depth_mm ? parseFloat(formData.tread_depth_mm) : null,
      mileage_at_install: formData.vehicle_id ? 0 : null,
      vehicle_id: formData.vehicle_id || null,
      position: formData.position || null,
    };
    await completeDialogSubmit({
      submit: () =>
        editingId
          ? axios.put(`${API}/tires/${editingId}`, payload)
          : axios.post(`${API}/tires`, payload),
      setDialogOpen: handleDialogOpenChange,
      setFormData,
      initialFormData: createInitialFormData,
      onSuccess: fetchData,
      successMessage: editingId ? 'Tire updated successfully!' : 'Tire added successfully!',
      errorMessage: editingId ? 'Failed to update tire' : 'Failed to add tire',
    });
    setEditingId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/tires/${deleteTarget.id}`);
      toast.success('Tire deleted');
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tire');
    } finally {
      setDeleting(false);
    }
  };

  const resetBulkDialog = () => {
    setBulkFile(null);
    setBulkResult(null);
    setBulkCountry(DEFAULT_COUNTRY_CODE);
  };

  const handleBulkDialogOpenChange = (open) => {
    setBulkDialogOpen(open);
    if (!open) resetBulkDialog();
  };

  const downloadBulkTemplate = async () => {
    try {
      const response = await axios.get(`${API}/tires/bulk-upload/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tire-import-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template');
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) {
      toast.error('Please select an Excel file to upload');
      return;
    }
    const uploadData = new FormData();
    uploadData.append('file', bulkFile);
    uploadData.append('country', bulkCountry);
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const { data } = await axios.post(`${API}/tires/bulk-upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(data);
      const imported = (data.created || 0) + (data.updated || 0);
      if (imported > 0) {
        fetchData();
        toast.success(
          `${data.created || 0} created · ${data.updated || 0} updated`
        );
      }
      if (data.failed > 0 && imported === 0) {
        toast.error('No tires were imported. Review the errors below.');
      } else if (data.failed > 0) {
        toast.warning(`${data.failed} row${data.failed === 1 ? '' : 's'} could not be imported`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const getTreadStatus = (tire) => {
    if (!tire.tread_depth_mm) return { status: 'unknown', color: 'slate' };
    if (tire.tread_depth_mm <= tire.min_tread_depth) return { status: 'Replace', color: 'red' };
    if (tire.tread_depth_mm <= tire.min_tread_depth + 1) return { status: 'Warning', color: 'amber' };
    return { status: 'Good', color: 'green' };
  };

  const getRotationStatus = (tire) => {
    if (!tire.next_rotation_due) return null;
    const dueDate = new Date(tire.next_rotation_due);
    const now = new Date();
    const daysUntil = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return { status: 'Overdue', color: 'red', days: Math.abs(daysUntil) };
    if (daysUntil <= 7) return { status: 'Due Soon', color: 'amber', days: daysUntil };
    return { status: 'OK', color: 'green', days: daysUntil };
  };

  const groupedByVehicle = vehicles.map(v => ({
    ...v,
    tires: tires.filter(t => t.vehicle_id === v.id && t.status === 'IN_USE'),
  }));

  const spareTires = tires.filter(t => t.status === 'SPARE' || !t.vehicle_id);

  return (
    <div className="p-6 lg:p-8" data-testid="tire-management-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Tire Management</h1>
          <p className="text-slate-600 mt-1">Track tire lifecycle, rotations, and replacements</p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <>
          <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button data-testid="add-tire-btn" onClick={openCreateDialog}>
                <Plus size={18} className="mr-2" />
                Add Tire
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit Tire' : 'Add New Tire'}</DialogTitle>
                <DialogDescription>Register a new tire with serial number tracking.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Serial Number *</Label>
                    <Input value={formData.serial_number} onChange={(e) => setFormData({...formData, serial_number: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Size *</Label>
                    <Input value={formData.size} onChange={(e) => setFormData({...formData, size: e.target.value})} placeholder="265/70R17" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Brand *</Label>
                    <Input value={formData.brand} onChange={(e) => setFormData({...formData, brand: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vehicle (optional)</Label>
                    <Select value={formData.vehicle_id || "NONE"} onValueChange={(value) => setFormData({...formData, vehicle_id: value === "NONE" ? "" : value})}>
                      <SelectTrigger><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">None (Spare)</SelectItem>
                        {vehicles.map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.registration_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Position</Label>
                    <Select value={formData.position} onValueChange={(value) => setFormData({...formData, position: value})}>
                      <SelectTrigger><SelectValue placeholder="Select position" /></SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Purchase Date *</Label>
                    <Input type="date" value={formData.purchase_date} onChange={(e) => setFormData({...formData, purchase_date: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Tread Depth (mm)</Label>
                    <Input type="number" step="0.1" value={formData.tread_depth_mm} onChange={(e) => setFormData({...formData, tread_depth_mm: e.target.value})} placeholder="8.0" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Cost *</Label>
                    <Input type="number" value={formData.purchase_cost} onChange={(e) => setFormData({...formData, purchase_cost: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GHS">GHS</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="LRD">LRD</SelectItem>
                        <SelectItem value="STN">STN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Country</Label>
                    <CountrySelect
                      value={formData.country}
                      onValueChange={(value) => setFormData({ ...formData, country: value })}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>Cancel</Button>
                  <Button type="submit">{editingId ? 'Save Changes' : 'Add Tire'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Button variant="outline" data-testid="bulk-upload-tires-btn" onClick={() => setBulkDialogOpen(true)}>
            <Upload size={18} className="mr-2" />
            Bulk Upload
          </Button>

          <Dialog open={bulkDialogOpen} onOpenChange={handleBulkDialogOpenChange}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Bulk Upload Tires</DialogTitle>
                <DialogDescription>
                  Import or update tires from Excel by serial number. Leave Registration Number blank for spares.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleBulkUpload} className="space-y-4">
                <div>
                  <Label>Default country (for blank Country cells)</Label>
                  <CountrySelect value={bulkCountry} onValueChange={setBulkCountry} />
                </div>
                <div>
                  <Button type="button" variant="outline" className="w-full" onClick={downloadBulkTemplate}>
                    <Download size={16} className="mr-2" />
                    Download sample template (.xlsx)
                  </Button>
                </div>
                <div>
                  <Label>Excel file</Label>
                  <Input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Columns: Serial Number, Brand, Model, Size, Country, Purchase Date, Purchase Cost, Currency,
                    Registration Number, Position, Mileage at Install, Tread Depth, Notes, Status.
                  </p>
                </div>
                {bulkResult && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-2">
                    <p className="font-medium text-slate-800">
                      Created {bulkResult.created} · Updated {bulkResult.updated} · Failed {bulkResult.failed}
                    </p>
                    {bulkResult.errors?.length > 0 && (
                      <ul className="max-h-32 overflow-y-auto text-red-700 space-y-1">
                        {bulkResult.errors.map((err, idx) => (
                          <li key={idx}>
                            {err.row ? `Row ${err.row}` : 'Import'}
                            {err.serial_number ? ` (${err.serial_number})` : ''}: {err.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => handleBulkDialogOpenChange(false)}>
                    Close
                  </Button>
                  <Button type="submit" disabled={bulkUploading}>
                    {bulkUploading ? 'Uploading…' : 'Upload & Import'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="fleet-card">
          <p className="text-sm text-slate-500">Total Tires</p>
          <p className="text-2xl font-bold text-slate-800">{tires.length}</p>
        </div>
        <div className="fleet-card">
          <p className="text-sm text-slate-500">In Use</p>
          <p className="text-2xl font-bold text-green-600">{tires.filter(t => t.status === 'IN_USE').length}</p>
        </div>
        <div className="fleet-card">
          <p className="text-sm text-slate-500">Spare Tires</p>
          <p className="text-2xl font-bold text-blue-600">{spareTires.length}</p>
        </div>
        <div className="fleet-card">
          <p className="text-sm text-slate-500">Need Replacement</p>
          <p className="text-2xl font-bold text-red-600">
            {tires.filter(t => t.tread_depth_mm && t.tread_depth_mm <= t.min_tread_depth).length}
          </p>
        </div>
      </div>

      {/* Tires by Vehicle */}
      <Tabs defaultValue="by-vehicle">
        <TabsList>
          <TabsTrigger value="by-vehicle">By Vehicle</TabsTrigger>
          <TabsTrigger value="all-tires">All Tires</TabsTrigger>
          <TabsTrigger value="spare">Spare Tires</TabsTrigger>
        </TabsList>

        <TabsContent value="by-vehicle" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {groupedByVehicle.map(vehicle => (
              <div key={vehicle.id} className="fleet-card">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{vehicle.registration_number}</h3>
                    <p className="text-sm text-slate-500">{vehicle.make} {vehicle.model}</p>
                  </div>
                  <span className={getCountryBadgeClass(vehicle.country)}>{getCountryLabel(vehicle.country)}</span>
                </div>
                
                {/* Tire Diagram */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {['FRONT_LEFT', 'FRONT_RIGHT', 'REAR_LEFT', 'REAR_RIGHT'].map(pos => {
                    const tire = vehicle.tires.find(t => t.position === pos);
                    const treadStatus = tire ? getTreadStatus(tire) : null;
                    const rotationStatus = tire ? getRotationStatus(tire) : null;
                    
                    return (
                      <div key={pos} className={`p-3 rounded-lg border-2 ${tire ? `border-${treadStatus?.color}-200 bg-${treadStatus?.color}-50` : 'border-slate-200 bg-slate-50'}`}>
                        <p className="text-xs text-slate-500 mb-1">{POSITIONS.find(p => p.value === pos)?.label}</p>
                        {tire ? (
                          <>
                            <p className="font-mono text-sm font-semibold">{tire.serial_number}</p>
                            <p className="text-xs text-slate-600">{tire.brand} {tire.size}</p>
                            {tire.tread_depth_mm && (
                              <p className={`text-xs text-${treadStatus?.color}-600`}>
                                Tread: {tire.tread_depth_mm}mm
                              </p>
                            )}
                            {rotationStatus && rotationStatus.status !== 'OK' && (
                              <p className={`text-xs text-${rotationStatus.color}-600 flex items-center gap-1`}>
                                <RotateCw size={10} /> Rotation {rotationStatus.status}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-slate-400">No tire assigned</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="all-tires" className="mt-4">
          <div className="fleet-card table-container">
            <table>
              <thead>
                <tr>
                  <th>Serial #</th>
                  <th>Brand/Model</th>
                  <th>Size</th>
                  <th>Vehicle</th>
                  <th>Position</th>
                  <th>Tread</th>
                  <th>Status</th>
                  <th>Country</th>
                  {canEdit && <th className="w-24">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {tires.map(tire => {
                  const vehicle = vehicles.find(v => v.id === tire.vehicle_id);
                  const treadStatus = getTreadStatus(tire);
                  return (
                    <tr key={tire.id}>
                      <td className="font-mono font-semibold">{tire.serial_number}</td>
                      <td>{tire.brand} {tire.model}</td>
                      <td>{tire.size}</td>
                      <td>{vehicle?.registration_number || '-'}</td>
                      <td>{POSITIONS.find(p => p.value === tire.position)?.label || '-'}</td>
                      <td>
                        {tire.tread_depth_mm ? (
                          <span className={`text-${treadStatus.color}-600 font-semibold`}>
                            {tire.tread_depth_mm}mm
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        <span className={`status-badge bg-${tire.status === 'IN_USE' ? 'green' : tire.status === 'SPARE' ? 'blue' : 'slate'}-100 text-${tire.status === 'IN_USE' ? 'green' : tire.status === 'SPARE' ? 'blue' : 'slate'}-800`}>
                          {tire.status}
                        </span>
                      </td>
                      <td><span className={getCountryBadgeClass(tire.country)}>{getCountryLabel(tire.country)}</span></td>
                      {canEdit && (
                        <td>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(tire)}>
                              <Pencil size={16} />
                            </Button>
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => setDeleteTarget(tire)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="spare" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {spareTires.map(tire => (
              <div key={tire.id} className="fleet-card">
                <h4 className="font-mono font-semibold text-lg">{tire.serial_number}</h4>
                <p className="text-slate-600">{tire.brand} {tire.model}</p>
                <p className="text-sm text-slate-500">{tire.size}</p>
                <div className="flex justify-between mt-2">
                  <span className={getCountryBadgeClass(tire.country)}>{getCountryLabel(tire.country)}</span>
                  <span className="text-sm text-slate-500">
                    {tire.tread_depth_mm ? `${tire.tread_depth_mm}mm tread` : 'New'}
                  </span>
                </div>
              </div>
            ))}
            {spareTires.length === 0 && (
              <p className="col-span-full text-center text-slate-500 py-8">No spare tires in inventory</p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        title="Delete tire?"
        description={deleteTarget ? `Permanently delete tire ${deleteTarget.serial_number}? This cannot be undone.` : undefined}
      />
    </div>
  );
};

export default TireManagement;
